import React, { useMemo, useState } from "react";
import { Database, RefreshCw, CheckCircle2, XCircle, Pencil, UploadCloud } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useKnowledgeBaseIntake } from "../../../hooks/useKnowledgeBaseIntake";
import { knowledgeBaseAPI } from "../../../services/knowledgeBaseAPI";
import { isSystemAdmin } from "../../../constants/systemRoles";

const titleCase = (v) =>
  String(v || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const statusClass = (value) => {
  const v = String(value || "").toLowerCase();
  if (["indexed", "accepted", "complete"].includes(v)) return "bg-green-100 text-green-800";
  if (["failed", "rejected"].includes(v)) return "bg-red-100 text-red-800";
  if (["indexing", "classifying", "awaiting_sync"].includes(v)) return "bg-yellow-100 text-yellow-800";
  return "bg-gray-100 text-gray-700";
};

const effective = (row, manual, detected) => row?.[manual] || row?.[detected] || "";

const REVIEW_THRESHOLD = 0.75;

const acceptanceState = (row, editing, draft) => {
  const payload = editing ? draft || {} : {};
  const type = payload.document_type || effective(row, "document_type", "detected_document_type");
  const category = payload.category || effective(row, "category", "detected_category");
  const discipline = payload.discipline || effective(row, "discipline", "detected_discipline");
  const approvalStatus = payload.approval_status || effective(row, "approval_status", "detected_approval_status");
  const currentRevision = editing
    ? payload.is_current_revision
    : (row.is_current_revision ?? row.detected_current_revision);
  const unknown = [type, category, discipline].some(
    (value) => !value || String(value).toLowerCase() === "unknown",
  );
  const authorityApproval = ["approved", "approved_as_noted", "issued_for_construction"].includes(
    String(approvalStatus || "").toLowerCase(),
  );
  const lowConfidence = (row.classification_confidence ?? 0) < REVIEW_THRESHOLD;
  const hasManualConfirmation = editing
    ? [payload.document_type, payload.category, payload.discipline].every(Boolean)
    : [row.document_type, row.category, row.discipline].every(Boolean);

  if (unknown) return { disabled: true, reason: "Complete the controlled classification fields before acceptance." };
  if (!authorityApproval) {
    return { disabled: true, reason: "Confirm Approved, Approved as Noted, or Issued for Construction before acceptance." };
  }
  if (currentRevision !== true) {
    return { disabled: true, reason: "Confirm this document is the current revision before acceptance." };
  }
  if (lowConfidence && !hasManualConfirmation) {
    return { disabled: true, reason: "Low-confidence classification must be reviewed and saved before acceptance." };
  }
  return { disabled: false, reason: "" };
};

const KnowledgeBaseIntakeView = () => {
  const { activeProject, currentUser, projectRole } = useProjectContext();
  const projectId = activeProject?.id;
  const { rows, options, loading, error, reload, run } = useKnowledgeBaseIntake(projectId);
  const admin = isSystemAdmin(currentUser?.role);
  const canReview = admin || projectRole === "qa_qc_manager";
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.document_name, row.detected_document_type, row.detected_category, row.detected_discipline, row.decision, row.kb_status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const pendingClassificationRows = useMemo(
    () => rows.filter((row) => row.classification_status === "pending_classification"),
    [rows],
  );
  const nextPendingClassification = pendingClassificationRows[0] || null;

  const beginEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      document_type: effective(row, "document_type", "detected_document_type"),
      category: effective(row, "category", "detected_category"),
      discipline: effective(row, "discipline", "detected_discipline"),
      revision: effective(row, "revision", "detected_revision"),
      approval_status: effective(row, "approval_status", "detected_approval_status"),
      is_current_revision:
        row.is_current_revision ?? row.detected_current_revision ?? "",
      decision_note: row.decision_note || "",
    });
  };

  const act = async (id, fn) => {
    setBusyId(id);
    try {
      await run(fn);
      setEditingId(null);
    } catch {
      // useKnowledgeBaseIntake exposes the API error in the page-level alert.
    } finally {
      setBusyId(null);
    }
  };

  if (!projectId) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Knowledge Base Intake</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Review AI-classified project documents before they are allowed to influence Requirement Intelligence.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canReview && (
            <button
              type="button"
              onClick={() => act("backfill", () => knowledgeBaseAPI.backfill(projectId))}
              disabled={busyId === "backfill"}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-sm hover:bg-gray-50 disabled:opacity-50"
              title="Add existing project documents to the intake queue. This does not call Bedrock."
            >
              <UploadCloud className="w-4 h-4" />
              {busyId === "backfill" ? "Backfilling…" : "Backfill Existing"}
            </button>
          )}
          {canReview && nextPendingClassification && (
            <button
              type="button"
              onClick={() =>
                act(nextPendingClassification.id, () =>
                  knowledgeBaseAPI.classify(projectId, nextPendingClassification.id),
                )
              }
              disabled={busyId != null}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-orange-300 bg-orange-50 text-orange-800 text-sm hover:bg-orange-100 disabled:opacity-50"
              title="Classify one queued document with Bedrock. One document per request avoids long batch timeouts."
            >
              Classify Next ({pendingClassificationRows.length})
            </button>
          )}
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search intake queue…"
        className="w-full border rounded-md px-3 py-2 text-sm"
      />

      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
          {error?.response?.data?.detail || error?.message || "Knowledge Base Intake request failed."}
        </div>
      )}

      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="text-left p-3">Document</th>
              <th className="text-left p-3">Uploaded by</th>
              <th className="text-left p-3">AI classification</th>
              <th className="text-left p-3">Confidence</th>
              <th className="text-left p-3">Decision</th>
              <th className="text-left p-3">KB status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-4 text-gray-500">Loading intake queue…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-gray-500">No Knowledge Base intake records found.</td></tr>
            )}
            {!loading && filtered.map((row) => {
              const editing = editingId === row.id;
              const acceptState = acceptanceState(row, editing, draft);
              return (
                <React.Fragment key={row.id}>
                  <tr className="border-t align-top">
                    <td className="p-3 min-w-[220px]">
                      <div className="font-medium text-gray-900">{row.document_name || `Document #${row.document_id}`}</div>
                      {row.file_url && <a href={row.file_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">Open document</a>}
                    </td>
                    <td className="p-3 text-gray-700">{row.uploaded_by_name || "—"}</td>
                    <td className="p-3 min-w-[230px]">
                      <div>{titleCase(effective(row, "document_type", "detected_document_type")) || "Unknown"}</div>
                      <div className="text-xs text-gray-500">
                        {titleCase(effective(row, "category", "detected_category")) || "Unknown"} · {titleCase(effective(row, "discipline", "detected_discipline")) || "Unknown"}
                      </div>
                      {row.classification_reason && <div className="text-xs text-gray-400 mt-1 max-w-[320px]">{row.classification_reason}</div>}
                    </td>
                    <td className="p-3">{row.classification_confidence == null ? "—" : `${Math.round(row.classification_confidence * 100)}%`}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs ${statusClass(row.decision)}`}>{titleCase(row.decision)}</span></td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs ${statusClass(row.kb_status)}`}>{titleCase(row.kb_status)}</span></td>
                    <td className="p-3 min-w-[260px]">
                      <div className="flex flex-wrap gap-2">
                        {canReview && (
                          <button onClick={() => beginEdit(row)} className="inline-flex items-center gap-1 px-2 py-1 border rounded text-xs"><Pencil className="w-3 h-3" /> Edit</button>
                        )}
                        {canReview && row.decision !== "accepted" && (
                          <button
                            disabled={busyId === row.id || acceptState.disabled}
                            title={acceptState.reason || "Accept this classified source into the Requirement Knowledge Base"}
                            onClick={() => act(row.id, () => knowledgeBaseAPI.accept(projectId, row.id, draft && editingId === row.id ? draft : {}))}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-600 text-white disabled:opacity-50"
                          ><CheckCircle2 className="w-3 h-3" /> Accept</button>
                        )}
                        {canReview && row.decision !== "rejected" && (
                          <button disabled={busyId === row.id} onClick={() => act(row.id, () => knowledgeBaseAPI.reject(projectId, row.id, draft && editingId === row.id ? draft : {}))} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-600 text-white disabled:opacity-50"><XCircle className="w-3 h-3" /> Reject</button>
                        )}
                        {canReview && row.decision === "accepted" && ["awaiting_sync", "failed"].includes(row.kb_status) && (
                          <button disabled={busyId === row.id} onClick={() => act(row.id, () => knowledgeBaseAPI.sync(projectId, row.id))} className="px-2 py-1 border rounded text-xs">Sync KB</button>
                        )}
                        {row.kb_ingestion_job_id && (
                          <button disabled={busyId === row.id} onClick={() => act(row.id, () => knowledgeBaseAPI.refresh(projectId, row.id))} className="px-2 py-1 border rounded text-xs">Refresh status</button>
                        )}
                        {canReview && row.classification_status === "pending_classification" && (
                          <button disabled={busyId === row.id} onClick={() => act(row.id, () => knowledgeBaseAPI.classify(projectId, row.id))} className="px-2 py-1 border rounded text-xs">Classify</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editing && (
                    <tr className="border-t bg-orange-50/40">
                      <td colSpan={7} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <SelectField label="Document type" value={draft.document_type} options={options.document_types || []} onChange={(v) => setDraft((p) => ({ ...p, document_type: v }))} />
                          <SelectField label="Category" value={draft.category} options={options.categories || []} onChange={(v) => setDraft((p) => ({ ...p, category: v }))} />
                          <SelectField label="Discipline" value={draft.discipline} options={options.disciplines || []} onChange={(v) => setDraft((p) => ({ ...p, discipline: v }))} />
                          <div><label className="text-xs text-gray-600">Revision</label><input value={draft.revision || ""} onChange={(e) => setDraft((p) => ({ ...p, revision: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm bg-white" /></div>
                          <SelectField label="Approval status" value={draft.approval_status} options={options.approval_statuses || []} onChange={(v) => setDraft((p) => ({ ...p, approval_status: v }))} />
                          <div><label className="text-xs text-gray-600">Current revision</label><select value={String(draft.is_current_revision ?? "")} onChange={(e) => setDraft((p) => ({ ...p, is_current_revision: e.target.value === "" ? null : e.target.value === "true" }))} className="w-full border rounded px-2 py-1.5 text-sm bg-white"><option value="">Select…</option><option value="true">Yes</option><option value="false">No</option></select></div>
                        </div>
                        <div className="mt-3"><label className="text-xs text-gray-600">Decision note</label><textarea rows={2} value={draft.decision_note || ""} onChange={(e) => setDraft((p) => ({ ...p, decision_note: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-sm bg-white" /></div>
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => act(row.id, () => knowledgeBaseAPI.updateClassification(projectId, row.id, draft))} className="px-3 py-1.5 rounded bg-orange-500 text-white text-sm">Save classification</button>
                          <button onClick={() => setEditingId(null)} className="px-3 py-1.5 rounded border bg-white text-sm">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SelectField = ({ label, value, options, onChange }) => (
  <div>
    <label className="text-xs text-gray-600">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm bg-white">
      <option value="">Select…</option>
      {options.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
    </select>
  </div>
);

export default KnowledgeBaseIntakeView;
